/**
 * Utility function to merge class names with Tailwind CSS
 * Similar to clsx but optimized for Tailwind
 */
export function cn(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(' ');
}

/**
 * Email validation utilities with robust validation
 */

/**
 * Validates a general email address format
 * Uses a more robust regex that follows RFC 5322 standards more closely
 * 
 * @param email - The email address to validate
 * @returns true if the email format is valid, false otherwise
 */
export function isValidEmail(email: string): boolean {
  if (!email || typeof email !== 'string') {
    return false;
  }

  // Trim whitespace
  const trimmedEmail = email.trim();
  
  // Basic length checks
  if (trimmedEmail.length === 0 || trimmedEmail.length > 254) {
    return false;
  }

  // More robust email regex that:
  // - Prevents leading/trailing dots
  // - Prevents consecutive dots
  // - Validates proper domain structure
  // - Ensures TLD is at least 2 characters
  // - Prevents invalid characters
  const emailRegex = /^(?!\.)(?!.*\.\.)(?!.*\.$)([a-zA-Z0-9!#$%&'*+\-/=?^_`{|}~]+(\.[a-zA-Z0-9!#$%&'*+\-/=?^_`{|}~]+)*)@([a-zA-Z0-9]([a-zA-Z0-9\-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
  
  if (!emailRegex.test(trimmedEmail)) {
    return false;
  }

  // Additional checks
  const [localPart, domain] = trimmedEmail.split('@');
  
  // Local part validation
  if (!localPart || localPart.length > 64) {
    return false;
  }
  
  // Domain validation
  if (!domain || domain.length > 253) {
    return false;
  }
  
  // Ensure domain has at least one dot (has TLD)
  if (!domain.includes('.')) {
    return false;
  }
  
  // Ensure domain doesn't start or end with dot or hyphen
  if (domain.startsWith('.') || domain.endsWith('.') || 
      domain.startsWith('-') || domain.endsWith('-')) {
    return false;
  }
  
  return true;
}

/**
 * Validates if an email is a Gmail address
 * 
 * @param email - The email address to validate
 * @returns true if the email is a valid Gmail address, false otherwise
 */
export function isValidGmail(email: string): boolean {
  if (!isValidEmail(email)) {
    return false;
  }

  const trimmedEmail = email.trim().toLowerCase();
  
  // Check if it ends with @gmail.com
  if (!trimmedEmail.endsWith('@gmail.com')) {
    return false;
  }
  
  // Extract local part
  const localPart = trimmedEmail.split('@')[0];
  
  // Gmail local part rules:
  // - Must be between 1 and 64 characters
  // - Can contain letters, numbers, dots, and plus signs
  // - Cannot start or end with a dot
  // - Cannot have consecutive dots
  if (!localPart || localPart.length === 0 || localPart.length > 64) {
    return false;
  }
  
  // Check for invalid patterns
  if (localPart.startsWith('.') || localPart.endsWith('.')) {
    return false;
  }
  
  if (localPart.includes('..')) {
    return false;
  }
  
  // Gmail allows: letters, numbers, dots, plus signs
  // Note: Gmail ignores dots and treats everything before + as the base address
  const gmailLocalPartRegex = /^[a-z0-9.+]+$/;
  if (!gmailLocalPartRegex.test(localPart)) {
    return false;
  }
  
  return true;
}

/**
 * Validates email and returns a detailed error message if invalid
 * 
 * @param email - The email address to validate
 * @param requireGmail - Whether to require Gmail addresses only
 * @returns Object with isValid boolean and error message string
 */
export function validateEmail(email: string, requireGmail: boolean = false): { isValid: boolean; error: string } {
  if (!email || typeof email !== 'string') {
    return { isValid: false, error: 'Email is required' };
  }

  const trimmedEmail = email.trim();
  
  if (trimmedEmail.length === 0) {
    return { isValid: false, error: 'Email cannot be empty' };
  }

  if (trimmedEmail.length > 254) {
    return { isValid: false, error: 'Email address is too long (maximum 254 characters)' };
  }

  if (!isValidEmail(trimmedEmail)) {
    return { isValid: false, error: 'Please enter a valid email address' };
  }

  if (requireGmail && !isValidGmail(trimmedEmail)) {
    return { isValid: false, error: 'Only Gmail addresses (@gmail.com) are accepted' };
  }

  return { isValid: true, error: '' };
}

